import React, { useState } from "react";
import {
  Heart,
  Coins,
  HandHeart,
  School,
  Home,
  Users,
  Phone,
  Copy,
  CheckCircle,
  HelpCircle,
  Utensils,
  Banknote,
} from "lucide-react";

const Donation = () => {
  const [copied, setCopied] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [activeMethod, setActiveMethod] = useState(null);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const donationAmounts = [
    { amount: 500, icon: <Utensils />, label: "একদিনের খাবার" },
    { amount: 1000, icon: <Heart />, label: "প্রাথমিক সাহায্য" },
    { amount: 5000, icon: <Coins />, label: "জীবন সমর্থক" },
    { amount: 10000, icon: <HandHeart />, label: "সাবলম্বীকরণ" },
  ];

  return (
    <div className="max-w-6xl mx-auto px-4 py-12">
      {/* Hero Section */}
      <div className="text-center mb-12">
        <Heart className="mx-auto text-primary mb-4" size={48} />
        <h1 className="text-4xl font-bold text-gray-800 mb-3">
          আজই পরিবর্তন আনুন
        </h1>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          আপনার সহায়তা আমাদের সমাজের জন্য কাজ করতে এবং ইতিবাচক পরিবর্তন আনতে
          সাহায্য করে।
        </p>
      </div>

      {/* Main Content */}
      <div className="border-2 rounded-xl border-primary bg-gradient-to-br from-primary/5 to-teal-50 p-6 shadow-lg mb-12">
        <h2 className="text-2xl font-semibold text-center mb-8 text-primary">
          আপনি কিভাবে দান করতে চান?
        </h2>

        {/* Donation Amounts */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {donationAmounts.map((item) => (
            <button
              key={item.amount}
              onClick={() => setSelectedAmount(item.amount)}
              className={`flex flex-col items-center justify-center p-6 rounded-lg transition-all hover:shadow-md ${
                selectedAmount === item.amount
                  ? "bg-primary text-white border-2 border-primary"
                  : "bg-white border border-gray-200 hover:border-primary text-gray-700"
              }`}
            >
              <div
                className={`p-3 rounded-full mb-3 ${
                  selectedAmount === item.amount
                    ? "bg-green-500"
                    : "bg-primary/5"
                }`}
              >
                {item.icon}
              </div>
              <span className="text-xl font-bold mb-1">৳{item.amount}</span>
              <span className="text-sm">{item.label}</span>
            </button>
          ))}
        </div>

        {/* Custom Amount */}
        <div className="w-full max-w-md mx-auto mb-12">
          <div className="relative">
            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-bold">
              ৳
            </span>
            <input
              type="number"
              placeholder="অথবা আপনার পছন্দের পরিমাণ লিখুন"
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              onChange={(e) => setSelectedAmount(parseInt(e.target.value))}
            />
          </div>
        </div>

        {/* Payment Methods */}
        <h3 className="text-xl font-semibold mb-6 text-center">
          পেমেন্ট পদ্ধতি নির্বাচন করুন
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-10">
          {/* bKash Method */}
          <div
            className={`border rounded-xl p-6 cursor-pointer transition-all hover:shadow-md ${
              activeMethod === "bkash"
                ? "border-2 border-pink-500 bg-pink-50"
                : "border-gray-200"
            }`}
            onClick={() => setActiveMethod("bkash")}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center mr-4 relative overflow-hidden">
                  <div className="text-pink-600 font-bold text-sm">bKash</div>
                </div>
                <span className="font-bold text-lg">বিকাশ</span>
              </div>
              {activeMethod === "bkash" && (
                <CheckCircle className="text-pink-500" />
              )}
            </div>

            {activeMethod === "bkash" && (
              <div className="mt-4 bg-white p-4 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-600">একাউন্ট নাম্বার:</span>
                  <div className="flex items-center">
                    <span className="font-mono mr-2">01880-658384</span>
                    <button
                      onClick={() => copyToClipboard("01880658384")}
                      className="text-primary hover:text-primary"
                    >
                      {copied ? <CheckCircle size={16} /> : <Copy size={16} />}
                    </button>
                  </div>
                </div>
                <div className="text-gray-600 text-sm">
                  একাউন্ট টাইপ: পারসোনাল (এনএনএস ফিনান্স টিম)
                </div>
                <div className="mt-3 text-sm bg-gray-50 p-2 rounded text-gray-600">
                  <ol className="list-decimal pl-4">
                    <li>*247# ডায়াল করে বিকাশ মেনুতে যান</li>
                    <li>"Send Money" অপশন নির্বাচন করুন</li>
                    <li>উপরের নাম্বারটি লিখুন</li>
                    <li>আপনার দানের পরিমাণ লিখুন</li>
                    <li>রেফারেন্স হিসাবে "নিজের নাম/NNS ID" লিখুন</li>
                    <li>আপনার বিকাশ পিন দিয়ে কনফার্ম করুন</li>
                    <li>উপরোক্ত নং এ ফোন দিয়ে নিশ্চিত হউন</li>
                  </ol>
                </div>
              </div>
            )}
          </div>

          {/* Nagad Method */}
          <div
            className={`border rounded-xl p-6 cursor-pointer transition-all hover:shadow-md ${
              activeMethod === "nagad"
                ? "border-2 border-orange-500 bg-orange-50"
                : "border-gray-200"
            }`}
            onClick={() => setActiveMethod("nagad")}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mr-4 relative overflow-hidden">
                  <div className="text-orange-600 font-bold text-sm">Nagad</div>
                </div>
                <span className="font-bold text-lg">নগদ</span>
              </div>
              {activeMethod === "nagad" && (
                <CheckCircle className="text-orange-500" />
              )}
            </div>

            {activeMethod === "nagad" && (
              <div className="mt-4 bg-white p-4 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-600">একাউন্ট নাম্বার:</span>
                  <div className="flex items-center">
                    <span className="font-mono mr-2">01880-658384</span>
                    <button
                      onClick={() => copyToClipboard("01880658384")}
                      className="text-primary hover:text-primary"
                    >
                      {copied ? <CheckCircle size={16} /> : <Copy size={16} />}
                    </button>
                  </div>
                </div>
                <div className="text-gray-600 text-sm">
                  একাউন্ট টাইপ: পারসোনাল (এনএনএস ফিনান্স টিম)
                </div>
                <div className="mt-3 text-sm bg-gray-50 p-2 rounded text-gray-600">
                  <ol className="list-decimal pl-4">
                    <li>*167# ডায়াল করে নগদ মেনুতে যান</li>
                    <li>"Send Money" অপশন নির্বাচন করুন</li>
                    <li>উপরের নাম্বারটি লিখুন</li>
                    <li>আপনার দানের পরিমাণ লিখুন</li>
                    <li>রেফারেন্স হিসাবে "নিজের নাম/NNS ID" লিখুন</li>
                    <li>আপনার নগদ পিন দিয়ে কনফার্ম করুন</li>
                    <li>উপরোক্ত নং এ ফোন দিয়ে নিশ্চিত হউন</li>
                  </ol>
                </div>
              </div>
            )}
          </div>

          {/* Bank Account Method */}
          <div
            className={`border rounded-xl p-6 cursor-pointer transition-all hover:shadow-md ${
              activeMethod === "bank"
                ? "border-2 border-blue-500 bg-blue-50"
                : "border-gray-200"
            }`}
            onClick={() => setActiveMethod("bank")}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4 relative overflow-hidden">
                  <Banknote className="text-blue-600" size={20} />
                </div>
                <span className="font-bold text-lg">ব্যাংক একাউন্ট</span>
              </div>
              {activeMethod === "bank" && (
                <CheckCircle className="text-blue-500" />
              )}
            </div>

            {activeMethod === "bank" && (
              <div className="mt-4 bg-white p-4 rounded-lg">
                <div className="space-y-3">
                  <div>
                    <div className="text-gray-600">ব্যাংক নাম:</div>
                    <div className="font-semibold">NRB GLOBAL BANK</div>
                  </div>
                  <div>
                    <div className="text-gray-600">একাউন্ট নাম্বার:</div>
                    <div className="flex items-center">
                      <span className="font-mono mr-2">0112000171118</span>
                      <button
                        onClick={() => copyToClipboard("0112000171118")}
                        className="text-primary hover:text-primary"
                      >
                        {copied ? (
                          <CheckCircle size={16} />
                        ) : (
                          <Copy size={16} />
                        )}
                      </button>
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-600">শাখা:</div>
                    <div className="font-semibold">Khatungonj Branch</div>
                  </div>
                  <div>
                    <div className="text-gray-600">রাউটিং নম্বর:</div>
                    <div className="flex items-center">
                      <span className="font-mono mr-2">300154272</span>
                      <button
                        onClick={() => copyToClipboard("300154272")}
                        className="text-primary hover:text-primary"
                      >
                        {copied ? (
                          <CheckCircle size={16} />
                        ) : (
                          <Copy size={16} />
                        )}
                      </button>
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-600">SWIFT কোড:</div>
                    <div className="flex items-center">
                      <span className="font-mono mr-2">NGBLBDDH</span>
                      <button
                        onClick={() => copyToClipboard("NGBLBDDH")}
                        className="text-primary hover:text-primary"
                      >
                        {copied ? (
                          <CheckCircle size={16} />
                        ) : (
                          <Copy size={16} />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Donation Impact */}
        <div className="bg-white rounded-xl p-6 shadow-sm mb-8">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <HelpCircle className="mr-2 text-primary" size={20} />
            আপনার দান কিভাবে সাহায্য করে
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 border border-gray-100 rounded-lg">
              <div className="text-lg font-bold mb-2 text-primary">৳1,000</div>
              <p className="text-gray-600 text-sm">
                ১টি পরিবারের জন্য প্রয়োজনীয় খাদ্য সরবরাহ করে
              </p>
            </div>
            <div className="p-4 border border-gray-100 rounded-lg">
              <div className="text-lg font-bold mb-2 text-primary">৳5,000</div>
              <p className="text-gray-600 text-sm">
                গরীব মেধাবী শিক্ষার্থীদের ১ মাসের শিক্ষা সামগ্রী বা চিকিৎসা
                সহায়তা
              </p>
            </div>
            <div className="p-4 border border-gray-100 rounded-lg">
              <div className="text-lg font-bold mb-2 text-primary">
                ৳10,000+
              </div>
              <p className="text-gray-600 text-sm">
                একটি পরিবার সাবলম্বীকরণ বা আশ্রয়ণ প্রকল্প
              </p>
            </div>
          </div>
        </div>

        {/* Donation Button */}
        <div className="text-center">
          <button
            className={`px-8 py-4 rounded-full text-white font-bold text-lg transition-all ${
              selectedAmount && activeMethod
                ? "bg-gradient-to-r from-primary to-teal-600 hover:from-green-700 hover:to-teal-700 shadow-lg hover:shadow-xl"
                : "bg-gray-400 cursor-not-allowed"
            }`}
            disabled={!selectedAmount || !activeMethod}
          >
            {selectedAmount ? `৳${selectedAmount} দান করুন` : "দান করুন"}
          </button>
          <p className="mt-4 text-sm text-gray-500">
            আপনার উদারতার জন্য ধন্যবাদ। কোনো বিষয়ে জিজ্ঞাসা থাকলে, আমাদের
            সাপোর্ট টিমের সাথে যোগাযোগ করুন।
          </p>
        </div>
      </div>

      {/* Additional Info */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 mb-12">
        <h3 className="text-xl font-semibold mb-6 text-center text-primary">
          আমাদের সম্পর্কে
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 bg-primary/5 rounded-full flex items-center justify-center mb-4">
              <Users className="text-primary" />
            </div>
            <h4 className="font-bold text-lg mb-2">আমাদের মিশন</h4>
            <p className="text-gray-600 text-center">
              বাংলাদেশের দরিদ্র ও সুবিধাবঞ্চিত মানুষদের জীবনমান উন্নয়নে সহায়তা
              করা।
            </p>
          </div>
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 bg-primary/5 rounded-full flex items-center justify-center mb-4">
              <School className="text-primary" />
            </div>
            <h4 className="font-bold text-lg mb-2">শিক্ষা সহায়তা</h4>
            <p className="text-gray-600 text-center">
              দরিদ্র শিক্ষার্থীদের জন্য বৃত্তি প্রোগ্রাম এবং শিক্ষা উপকরণ বিতরণ।
            </p>
          </div>
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 bg-primary/5 rounded-full flex items-center justify-center mb-4">
              <Home className="text-primary" />
            </div>
            <h4 className="font-bold text-lg mb-2">কমিউনিটি সাপোর্ট</h4>
            <p className="text-gray-600 text-center">
              দুর্যোগ মোকাবেলায় সহায়তা, খাদ্য বিতরণ এবং সামাজিক সেবামূলক
              কার্যক্রম।
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Donation;
